// Centralized helpers for persisting and hydrating the selected workspace across sessions/tabs
// We keep sessionStorage (for fast access) and localStorage as a durable backup.

export type WorkspaceSnapshot = {
  workspaceId: string;
  workspaceName?: string | null;
  userId?: string | null;
  role?: string | null;
  authUserId?: string | null;
};

const SS_ID_KEY = 'currentWorkspaceId';
const SS_DATA_KEY = 'workspaceData';
const LS_LAST_ID_KEY = 'lastWorkspaceId';

export function saveWorkspaceSelection(snapshot: WorkspaceSnapshot) {
  const id = String(snapshot.workspaceId);
  try { sessionStorage.setItem(SS_ID_KEY, id); } catch (_) {}
  try { sessionStorage.setItem(SS_DATA_KEY, JSON.stringify(snapshot)); } catch (_) {}
  try { localStorage.setItem(LS_LAST_ID_KEY, id); } catch (_) {}
  try { localStorage.setItem(`workspaceData:${id}`, JSON.stringify(snapshot)); } catch (_) {}
}

export function loadWorkspaceId(): string | null {
  // 1) Try sessionStorage id first
  try {
    const id = sessionStorage.getItem(SS_ID_KEY);
    const raw = sessionStorage.getItem(SS_DATA_KEY);
    if (id && raw) {
      try {
        const parsed = JSON.parse(raw);
        if (String(parsed?.workspaceId ?? '') === String(id)) return id;
      } catch (_) {}
    }
  } catch (_) {}

  // 2) If missing/mismatched, try to rehydrate from localStorage backup
  let lastId: string | null = null;
  try { lastId = localStorage.getItem(LS_LAST_ID_KEY); } catch (_) {}
  if (lastId) {
    let rawLocal: string | null = null;
    try { rawLocal = localStorage.getItem(`workspaceData:${lastId}`); } catch (_) {}
    if (rawLocal) {
      try {
        const parsed = JSON.parse(rawLocal);
        if (String(parsed?.workspaceId ?? '') === String(lastId)) {
          // Rehydrate session for current tab
          try { sessionStorage.setItem(SS_ID_KEY, lastId); } catch (_) {}
          try { sessionStorage.setItem(SS_DATA_KEY, JSON.stringify(parsed)); } catch (_) {}
          return lastId;
        }
      } catch (_) {}
    }
  }

  return null;
}

export function clearWorkspaceSelection() {
  try { sessionStorage.removeItem(SS_ID_KEY); } catch (_) {}
  try { sessionStorage.removeItem(SS_DATA_KEY); } catch (_) {}
  try { localStorage.removeItem(LS_LAST_ID_KEY); } catch (_) {}
  // Do not iterate all workspaceData:* keys to avoid heavy ops; optional cleanup can be added elsewhere
}
